import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BookmarkIcon,
  BookOpenIcon,
  CubeTransparentIcon,
  DevicePhoneMobileIcon,
  PencilIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import translations from "../../assets/translations.json";
import { RabbitHoleEditionCover } from "../../components/RabbitHoleEditionCover";
import { useGeneratedTextCreate } from "../../hooks/useGeneratedTextCreate";
import { useGeneratedTextDetails } from "../../hooks/useGeneratedTextDetails";
import { usePaginatedVocabularyList, type VocabularyDto } from "../../hooks/usePaginatedVocabularyList";
import { usePersonalEditionStoryIdeas } from "../../hooks/usePersonalEditionStoryIdeas";

export const Route = createFileRoute("/app/personal_editions")({
  validateSearch: (search: Record<string, unknown>) => ({
    editionId:
      typeof search.editionId === "number" && Number.isInteger(search.editionId)
        ? search.editionId
        : typeof search.editionId === "string" && /^\d+$/.test(search.editionId)
          ? Number(search.editionId)
          : undefined,
  }),
  component: PersonalEditionsPage,
});

type Step =
  | "about"
  | "words"
  | "idea"
  | "length"
  | "preview-generating"
  | "preview"
  | "generating"
  | "ready"
  | "reader";
type CoverPalette = "violet" | "coral" | "sage";

const COPY = {
  title: "Notte a Tokyo",
  scene:
    "Una donna perde l’ultimo treno a Tokyo e finisce per passare la notte con un gruppo di sconosciuti.",
  sample: [
    "La città era già silenziosa quando mi sono resa conto di aver perso l’ultimo treno.",
    "Non avevo un piano. Solo una valigia leggera e il vento di Tokyo che sapeva di pioggia.",
    "Davanti alla stazione, una donna con un ombrello rosso mi ha chiesto se stessi bene. Ho sorriso, anche se non sapevo cosa dire.",
    "Abbiamo camminato fino a un piccolo bar ancora aperto, dove le luci calde facevano sembrare la notte meno sconosciuta.",
  ],
};

const FALLBACK_STORY_IDEAS = [
  "summer",
  "museum",
  "market",
  "train",
  "notebook",
];

const PERSONAL_EDITION_WORD_DRAFT_KEY = "uxhulu:personal-edition:selected-italian-words";

function storyPages(paragraphs: string[], wordsPerPage = 90): string[] {
  const words = paragraphs.join("\n\n").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  return Array.from(
    { length: Math.ceil(words.length / wordsPerPage) },
    (_, pageIndex) =>
      words.slice(pageIndex * wordsPerPage, (pageIndex + 1) * wordsPerPage).join(" "),
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightEditionWords(text: string, words: string[]): ReactNode {
  const terms = [...new Set(words.map((word) => word.trim()).filter(Boolean))]
    .sort((left, right) => right.length - left.length);
  if (terms.length === 0) return text;

  const matcher = new RegExp(
    `(^|[^\\p{L}\\p{M}])(${terms.map(escapeRegExp).join("|")})(?=$|[^\\p{L}\\p{M}])`,
    "giu",
  );
  const parts: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = matcher.exec(text)) !== null) {
    const prefix = match[1] ?? "";
    const word = match[2] ?? "";
    const start = match.index + prefix.length;
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <mark
        className="personal-edition-sample-reader__word"
        key={`${start}-${word}`}
      >
        {word}
      </mark>,
    );
    cursor = start + word.length;
  }

  if (cursor === 0) return text;
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

function BookCover({
  title = COPY.title,
  palette = "violet",
  compact = false,
  intro = false,
}: {
  title?: string;
  palette?: CoverPalette;
  compact?: boolean;
  intro?: boolean;
}) {
  const color =
    palette === "coral"
      ? "#ef9b8b"
      : palette === "sage"
        ? "#9cae98"
        : "#6574bc";
  return (
    <div
      className={`personal-edition-book ${compact ? "personal-edition-book--compact" : ""} ${intro ? "personal-edition-book--intro" : ""}`}
      style={{ "--edition-accent": color } as CSSProperties}
      aria-label={`${title} cover`}
    >
      <div className="personal-edition-book__back" />
      <div className="personal-edition-book__pages" />
      <div className="personal-edition-cover">
        <div className="personal-edition-cover__spine" />
        <div className="personal-edition-cover__star">✦</div>
        <p>{title}</p>
        <div className="personal-edition-cover__bloom" />
      </div>
    </div>
  );
}

function SharedPersonalEditionCover() {
  return (
    <div
      className="personal-edition-overview-cover"
      aria-label="Book No. 01 cover"
    >
      <RabbitHoleEditionCover
        topic="Personal Edition"
        title=""
        language="ITALIAN"
        languageLabel="No. 01"
        weekStart="2026-08-31"
        weekEnd="2026-08-31"
        dayIndex={0}
        completed={0}
        total={1}
        dayLabel="Italiano"
        progressLabel="August 2026"
        verticalLabel=""
        dateRangeLabel=""
        variant="mini"
        showTopicHeader={false}
        personal
        testId="personal-editions-about-cover"
      />
    </div>
  );
}

function PhoneTop({ back, onBack }: { back?: boolean; onBack?: () => void }) {
  return (
    <header className="personal-edition-top">
      {back ? (
        <button type="button" onClick={onBack} aria-label="Back">
          <ArrowLeftIcon />
        </button>
      ) : null}
      <span className="personal-edition-top__label">
        <em>Italian</em> Personal Edition
      </span>
      <span className="personal-edition-top__spacer" aria-hidden="true" />
    </header>
  );
}

function PrimaryButton({
  children,
  onClick,
  testId,
  inline = false,
  bottom = false,
  disabled = false,
}: {
  children: ReactNode;
  onClick: () => void;
  testId?: string;
  inline?: boolean;
  bottom?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`personal-edition-edition-action personal-edition-primary ${inline ? "personal-edition-primary--inline" : ""} ${bottom ? "personal-edition-primary--bottom" : ""}`}
    >
      {children}
      <ArrowRightIcon />
    </button>
  );
}

function IdeaEditor({
  scene,
  suggestions,
  onSceneChange,
  onContinue,
}: {
  scene: string;
  suggestions: string[];
  onSceneChange: (value: string) => void;
  onContinue: () => void;
}) {
  const translate = (key: keyof typeof translations) =>
    translations[key].ENGLISH;
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const surpriseTopic =
    "A weekend in Naples, trying local food and exploring the city.";
  const hasIdea = scene.trim().length > 0;
  const resizeEditor = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.style.height = "auto";
    const verticalBorder = editor.offsetHeight - editor.clientHeight;
    const naturalHeight = editor.scrollHeight + verticalBorder;
    // Keep ordinary ideas fully visible. Only introduce an inner scroll once a
    // long composition would take space from the suggestions and action below.
    const availableHeight = Math.max(220, Math.min(340, window.innerHeight - 440));
    const nextHeight = Math.min(naturalHeight, availableHeight);
    editor.style.height = `${nextHeight}px`;
    editor.style.overflowY = naturalHeight > availableHeight ? "auto" : "hidden";
  };
  useEffect(() => {
    resizeEditor();
  }, [scene]);
  const placeCursorAtEnd = () => {
    const editor = editorRef.current;
    if (!editor) return;
    window.requestAnimationFrame(() =>
      editor.setSelectionRange(editor.value.length, editor.value.length),
    );
  };
  return (
    <section className="personal-edition-idea-editor">
      <h1>{translate("personal_editions_idea_title")}</h1>
      <div className="personal-edition-idea-editor__field">
        <textarea
          ref={editorRef}
          value={scene}
          placeholder={`${translate("personal_editions_idea_intro")}\n${translate("personal_editions_idea_helper")}`}
          onFocus={placeCursorAtEnd}
          onClick={placeCursorAtEnd}
          onChange={(event) => onSceneChange(event.target.value)}
          aria-label={translate("personal_editions_idea_title")}
          maxLength={200}
        />
        {scene ? (
          <button
            data-testid="personal-editions-idea-clear"
            type="button"
            className="personal-edition-idea-editor__clear"
            onClick={() => onSceneChange("")}
            aria-label={translate("personal_editions_idea_clear")}
          >
            ×
          </button>
        ) : null}
        <span className="personal-edition-idea-editor__count" aria-live="polite">
          {scene.length}/200
        </span>
      </div>
      <section className="personal-edition-idea-editor__suggestions" aria-label={translate("personal_editions_idea_suggestions")}>
        <h2>{translate("personal_editions_idea_suggestions")}</h2>
        <div>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              data-testid={`personal-editions-idea-suggestion-${suggestion.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
              type="button"
              onClick={() => onSceneChange(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </section>
      <button
        data-testid="personal-editions-idea-surprise"
        type="button"
        className={`personal-edition-idea-editor__surprise ${hasIdea ? "is-continue" : ""}`}
        onClick={() => hasIdea ? onContinue() : onSceneChange(surpriseTopic)}
      >
        <ArrowsRightLeftIcon />
        <span>
          <b>{translate(hasIdea ? "personal_editions_continue" : "personal_editions_idea_surprise_title")}</b>
          <small>{translate(hasIdea ? "personal_editions_idea_surprise_continue_body" : "personal_editions_idea_surprise_body")}</small>
        </span>
        <ArrowRightIcon />
      </button>
    </section>
  );
}

function LengthSelection({
  length,
  onLengthChange,
}: {
  length: "short" | "long" | "book";
  onLengthChange: (value: "short" | "long" | "book") => void;
}) {
  const translate = (key: keyof typeof translations) =>
    translations[key].ENGLISH;
  const options = [
    { value: "short", title: "personal_editions_length_short", detail: "personal_editions_length_short_detail", icon: "single" },
    { value: "long", title: "personal_editions_length_long", detail: "personal_editions_length_long_detail", icon: "open" },
    { value: "book", title: "personal_editions_length_book", detail: "personal_editions_length_book_detail", icon: "book" },
  ] as const;
  const FormatIcon = ({ kind }: { kind: "single" | "open" | "book" }) => {
    if (kind === "single") {
      return (
        <svg className="personal-edition-lengths__illustration" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <path d="M16 8h25l7 7v38c-11 3-21 3-32 0V8Z" />
          <path d="M41 8v7h7" />
          <path d="M23 23h18M23 30h18M23 37h12" />
        </svg>
      );
    }
    if (kind === "open") {
      return (
        <svg className="personal-edition-lengths__illustration" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <path d="M8 14c8-4 16-4 24 0v36c-8-4-16-4-24 0V14Z" />
          <path d="M56 14c-8-4-16-4-24 0v36c8-4 16-4 24 0V14Z" />
          <path d="M32 14v36M15 25h10M15 32h10M39 25h10M39 32h10" />
        </svg>
      );
    }
    return (
      <svg className="personal-edition-lengths__illustration" viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <path d="M18 9h29v42H18V9Z" />
        <path d="M18 51h29v5H18zM23 9v42M18 51c0-3 2-5 5-5h24" />
        <path d="M29 20h12M29 27h12" />
      </svg>
    );
  };
  return (
    <section className="personal-edition-form personal-edition-length-step">
      <h1>{translate("personal_editions_length_title")}</h1>
      <p className="personal-edition-description">
        {translate("personal_editions_length_prompt")}
      </p>
      <div className="personal-edition-lengths" role="radiogroup" aria-label={translate("personal_editions_length_title")}>
        {options.map((option) => (
          <button type="button" role="radio" aria-checked={length === option.value} key={option.value} className={length === option.value ? "is-selected" : ""} onClick={() => onLengthChange(option.value)}>
            <FormatIcon kind={option.icon} />
            <span className="personal-edition-lengths__copy"><b>{translate(option.title)}</b><span>{translate(option.detail)}</span></span>
            <span className="personal-edition-lengths__radio" aria-hidden="true" />
          </button>
        ))}
      </div>
    </section>
  );
}

function editionWordSource(word: VocabularyDto): string | null {
  const usage = word.usages.find((item) => item.source_title?.trim());
  return usage?.source_title ?? null;
}

function wordAddedLabel(word: VocabularyDto, index: number): string | null {
  const translate = (key: keyof typeof translations) => translations[key].ENGLISH;
  if (word.created_at) {
    const days = Math.max(
      0,
      Math.floor((Date.now() - new Date(word.created_at).getTime()) / 86_400_000),
    );
    if (days === 0) return translate("personal_editions_words_added_today");
    if (days === 1) return translate("personal_editions_words_added_yesterday");
    if (days < 7) return `${translate("personal_editions_words_added")} ${days} ${translate("personal_editions_words_days_ago")}`;
    if (days < 14) return translate("personal_editions_words_added_week_ago");
  }
  const fallbackDays = [1, 2, 3, 5] as const;
  if (index === 4) return translate("personal_editions_words_added_week_ago");
  if (fallbackDays[index] === undefined) return null;
  if (fallbackDays[index] === 1) return translate("personal_editions_words_added_yesterday");
  return `${translate("personal_editions_words_added")} ${fallbackDays[index]} ${translate("personal_editions_words_days_ago")}`;
}

function EditionWordRow({
  word,
  index,
  editable = false,
  checked = false,
  selectionState,
  disabled = false,
  onToggle,
}: {
  word: VocabularyDto;
  index: number;
  editable?: boolean;
  checked?: boolean;
  selectionState?: boolean;
  disabled?: boolean;
  onToggle?: () => void;
}) {
  const source = editionWordSource(word);
  const added = wordAddedLabel(word, index);
  return (
    <div className={`personal-edition-words__item ${editable ? "is-editable" : ""}`}>
      <div className="personal-edition-words__word">
        <b>{word.word}</b>
        <small className="personal-edition-words__translation">{word.translation}</small>
        {source ? (
          <p className="personal-edition-words__source">
            <BookOpenIcon aria-hidden="true" />
            <span>{source}</span>
          </p>
        ) : null}
      </div>
      {editable ? (
        <label className="personal-edition-words__toggle">
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onToggle}
            aria-label={`Use ${word.word} in my Edition`}
          />
          <span className="personal-edition-words__check" aria-hidden="true">✓</span>
        </label>
      ) : selectionState !== undefined ? (
        <span
          className={`personal-edition-words__selection-state ${selectionState ? "is-selected" : ""}`}
          aria-hidden="true"
        >
          ✓
        </span>
      ) : added ? (
        <small className="personal-edition-words__added">{added}</small>
      ) : null}
    </div>
  );
}

function WordSelectionStep({
  words,
  selectedWords,
  onToggle,
  onBack,
  onContinue,
}: {
  words: VocabularyDto[];
  selectedWords: Set<number>;
  onToggle: (wordId: number) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const translate = (key: keyof typeof translations) =>
    translations[key].ENGLISH;
  const [isEditing, setIsEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const selected = words.filter((word) => selectedWords.has(word.id));
  const previewWords = selected.slice(0, 4);
  const hasCustomizedSelection = words.some((word) => !selectedWords.has(word.id));
  const reviewWords = hasCustomizedSelection ? words : showAll ? selected : previewWords;
  const remainingWords = Math.max(0, selected.length - previewWords.length);
  const canDeselect = selectedWords.size > 10;

  return (
    <div className="personal-edition-words-screen">
      <PhoneTop back onBack={onBack} />
      <section className={`personal-edition-words ${isEditing ? "is-editing" : "is-reviewing"}`}>
        <h1>{translate("personal_editions_words_review_title")}</h1>
        <p className="personal-edition-description">
          {translate("personal_editions_words_review_body_before_count")} {selectedWords.size} {translate("personal_editions_words_review_body_after_count")}
        </p>
        {isEditing ? (
          <>
            <div className="personal-edition-words__selection-summary" aria-live="polite">
              <span>{selectedWords.size} {translate("personal_editions_words_selected")}</span>
            </div>
            <div className="personal-edition-words__list" aria-label={translate("personal_editions_words_edit_title")}>
              {words.map((word, index) => (
                <EditionWordRow
                  key={word.id}
                  word={word}
                  index={index}
                  editable
                  checked={selectedWords.has(word.id)}
                  disabled={selectedWords.has(word.id) && !canDeselect}
                  onToggle={() => onToggle(word.id)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="personal-edition-words__review-meta">
              <span>{selectedWords.size} {translate("personal_editions_words_review_count")}</span>
              <button
                type="button"
                data-testid="personal-editions-words-change"
                onClick={() => setIsEditing(true)}
              >
                {translate("personal_editions_words_change")} <PencilIcon aria-hidden="true" />
              </button>
            </div>
            <div className="personal-edition-words__list" aria-label={translate("personal_editions_words_review_title")}>
              {reviewWords.map((word, index) => (
                <EditionWordRow
                  key={word.id}
                  word={word}
                  index={index}
                  selectionState={hasCustomizedSelection ? selectedWords.has(word.id) : undefined}
                />
              ))}
              {!hasCustomizedSelection && !showAll && remainingWords > 0 ? (
                <button
                  type="button"
                  className="personal-edition-words__more"
                  onClick={() => setShowAll(true)}
                >
                  … {translate("personal_editions_words_and")} {remainingWords} {translate("personal_editions_words_more")}
                </button>
              ) : null}
            </div>
          </>
        )}
      </section>
      <footer className="personal-edition-words-footer">
        <button
          data-testid="personal-editions-words-continue"
          type="button"
          className="personal-edition-words__action is-continue"
          onClick={isEditing ? () => { setIsEditing(false); setShowAll(false); } : onContinue}
        >
          <BookOpenIcon aria-hidden="true" />
          <span>
            <b>{translate(isEditing ? "personal_editions_words_done" : "personal_editions_continue")}</b>
            <small>{isEditing ? `${selectedWords.size} ${translate("personal_editions_words_selected")}` : translate("personal_editions_words_action_body")}</small>
          </span>
          <ArrowRightIcon aria-hidden="true" />
        </button>
      </footer>
    </div>
  );
}

function PersonalEditionIntroduction({
  onDismiss,
  onBack,
}: {
  onDismiss: () => void;
  onBack: () => void;
}) {
  const translate = (key: keyof typeof translations) =>
    translations[key].ENGLISH;
  return (
    <div className="personal-edition-introduction-backdrop" role="presentation">
      <div className="personal-edition-introduction-top">
        <PhoneTop back onBack={onBack} />
      </div>
      <section
        className="personal-edition-introduction-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="About Personal Editions"
      >
        <h1>{translate("personal_editions_introduction_title")}</h1>
        <div className="personal-edition-introduction-formats">
          <article
            className="personal-edition-digital-preview"
            aria-label="Digital Edition preview"
          >
            <div className="personal-edition-digital-preview__screen">
              <header>
                <ArrowLeftIcon />
                <b>Capitolo 1</b>
                <span>Aa</span>
                <BookmarkIcon />
              </header>
              <h2>
                Una nuova
                <br />
                prospettiva
              </h2>
              <p>
                La pioggia cadeva leggera sulla città, come un sussurro. Mia
                sorella rideva, tenendo in mano una tazza di caffè caldo.
              </p>
              <p>
                «Oggi è il giorno giusto per cambiare prospettiva», disse.
                Annuì, guardando fuori dalla finestra.
              </p>
              <footer>
                <span>12%</span>
                <span>Capitolo 1 di 8</span>
              </footer>
            </div>
          </article>
          <div className="personal-edition-introduction-cover">
            <SharedPersonalEditionCover />
          </div>
        </div>
        <button
          type="button"
          className="personal-edition-introduction-dismiss is-create"
          data-testid="personal-editions-introduction-dismiss"
          onClick={onDismiss}
        >
          <BookOpenIcon aria-hidden="true" />
          <span>
            <b>{translate("personal_editions_introduction_create")}</b>
            <small>{translate("personal_editions_introduction_create_body")}</small>
          </span>
          <ArrowRightIcon />
        </button>
        <div className="personal-edition-introduction-options-heading">
          <span />
          <p>{translate("personal_editions_introduction_options_heading")}</p>
          <span />
        </div>
        <div className="personal-edition-introduction-options">
          <div>
            <DevicePhoneMobileIcon />
            <p>
              <b>{translate("personal_editions_introduction_read_title")}</b>
              <span>
                {translate("personal_editions_introduction_read_body")}
              </span>
            </p>
          </div>
          <div>
            <BookOpenIcon />
            <p>
              <b>{translate("personal_editions_introduction_print_title")}</b>
              <span>
                {translate("personal_editions_introduction_print_body")}
              </span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function PersonalEditionsPage() {
  const translate = (key: keyof typeof translations) =>
    translations[key].ENGLISH;
  const navigate = useNavigate();
  const { editionId } = Route.useSearch();
  const storedEditionQuery = useGeneratedTextDetails(editionId);
  const personalEditionStoryIdeasQuery = usePersonalEditionStoryIdeas();
  const restoredEditionId = useRef<number | null>(null);
  const [step, setStep] = useState<Step>("about");
  const [scene, setScene] = useState("");
  const [length, setLength] = useState<"short" | "long" | "book">("long");
  const [palette, setPalette] = useState<CoverPalette>("violet");
  const vocabularyQuery = usePaginatedVocabularyList({
    word_languages: ["ITALIAN"],
    page_size: 30,
  });
  const savedItalianWords = (
    vocabularyQuery.data?.pages.flatMap((page) => page.results) ?? []
  ).slice(0, 30);
  const storyIdeaSuggestions = useMemo(() => {
    if (personalEditionStoryIdeasQuery.data?.length) {
      return personalEditionStoryIdeasQuery.data;
    }
    return FALLBACK_STORY_IDEAS;
  }, [personalEditionStoryIdeasQuery.data]);
  const [selectedWordsInitialized, setSelectedWordsInitialized] = useState(false);
  const [hasAcknowledgedIntroduction, setHasAcknowledgedIntroduction] =
    useState(false);
  const [selectedWords, setSelectedWords] = useState<Set<number>>(() => new Set());
  const effectiveSelectedWords = useMemo(
    () =>
      selectedWordsInitialized
        ? selectedWords
        : new Set(savedItalianWords.map((word) => word.id)),
    [savedItalianWords, selectedWords, selectedWordsInitialized],
  );
  const [generatedTextId, setGeneratedTextId] = useState<number | null>(null);
  const [previewTextId, setPreviewTextId] = useState<number | null>(null);
  const [editionTitle, setEditionTitle] = useState(COPY.title);
  const [editionStory, setEditionStory] = useState(COPY.sample);
  const [readerPage, setReaderPage] = useState(0);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewStory, setPreviewStory] = useState<string[]>([]);
  const [generationFailed, setGenerationFailed] = useState(false);
  const [previewGenerationFailed, setPreviewGenerationFailed] = useState(false);
  const createGeneratedText = useGeneratedTextCreate();
  const readerPages = storyPages(editionStory);
  const selectedWordTerms = savedItalianWords
    .filter((word) => effectiveSelectedWords.has(word.id))
    .map((word) => word.word);
  useEffect(() => {
    const storedEdition = storedEditionQuery.data;
    if (
      !storedEdition ||
      storedEdition.status !== "COMPLETE" ||
      restoredEditionId.current === storedEdition.id
    ) {
      return;
    }
    const metadata = storedEdition.image_import_metadata || {};
    const isPersonalEdition =
      metadata.personal_edition === true ||
      (storedEdition.text_type === "STORY" &&
        !storedEdition.is_review_practice &&
        (storedEdition.struggle_words?.length ?? 0) > 0);
    if (!isPersonalEdition) return;
    restoredEditionId.current = storedEdition.id;
    setEditionTitle(storedEdition.topic || COPY.title);
    setEditionStory(
      storedEdition.chatgpt_output.split(/\n\s*\n/).filter(Boolean),
    );
    setReaderPage(0);
    setStep("reader");
  }, [storedEditionQuery.data]);
  useEffect(() => {
    if (selectedWordsInitialized || savedItalianWords.length === 0) return;
    const availableIds = new Set(savedItalianWords.map((word) => word.id));
    let restoredIds: number[] = [];
    try {
      const stored = window.sessionStorage.getItem(PERSONAL_EDITION_WORD_DRAFT_KEY);
      const parsed: unknown = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed)) {
        restoredIds = parsed.filter(
          (id): id is number => typeof id === "number" && availableIds.has(id),
        );
      }
    } catch {
      // A malformed draft is safely replaced with the learner's available words.
    }
    setSelectedWords(
      new Set(restoredIds.length > 0 ? restoredIds : savedItalianWords.map((word) => word.id)),
    );
    setSelectedWordsInitialized(true);
  }, [savedItalianWords, selectedWordsInitialized]);
  useEffect(() => {
    if (!selectedWordsInitialized) return;
    window.sessionStorage.setItem(
      PERSONAL_EDITION_WORD_DRAFT_KEY,
      JSON.stringify([...selectedWords]),
    );
  }, [selectedWords, selectedWordsInitialized]);
  useEffect(() => {
    const isPreview = step === "preview-generating";
    const activeTextId = isPreview ? previewTextId : generatedTextId;
    if ((step !== "generating" && !isPreview) || activeTextId === null) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`/api/reeprise/generated_texts/${activeTextId}/`);
        if (!response.ok || cancelled) return;
        const text = await response.json() as { status: "PENDING" | "COMPLETE" | "FAILED"; topic: string; chatgpt_output: string };
        if (text.status === "COMPLETE") {
          const paragraphs = text.chatgpt_output.split(/\n\s*\n/).filter(Boolean);
          if (isPreview) {
            setPreviewTitle(text.topic || scene);
            setPreviewStory(paragraphs);
            setStep("preview");
          } else {
            setEditionTitle(text.topic || COPY.title);
            setEditionStory(paragraphs);
            setStep("ready");
          }
        }
        if (text.status === "FAILED") {
          if (isPreview) setPreviewGenerationFailed(true);
          else setGenerationFailed(true);
        }
      } catch {
        // Keep the calm generation state visible while the next poll retries.
      }
    };
    void poll();
    const interval = window.setInterval(() => void poll(), 1800);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [generatedTextId, previewTextId, scene, step]);
  const toggleWord = (wordId: number) =>
    setSelectedWords((current) => {
      const next = new Set(current);
      if (next.has(wordId)) next.delete(wordId);
      else next.add(wordId);
      return next;
    });
  const startGeneration = async () => {
    if (effectiveSelectedWords.size === 0) return;
    setGenerationFailed(false);
    setStep("generating");
    try {
      const text = await createGeneratedText.mutateAsync({
        createReepriseDto: {
          text_type: "STORY",
          language: "ITALIAN",
          level: "A2",
          tense: "A",
          word_number: length === "short" ? 100 : length === "long" ? 200 : 300,
          topic: scene,
          main_character: "",
          edition_word_ids: [...effectiveSelectedWords],
        },
      });
      setGeneratedTextId(text.id);
    } catch {
      setGenerationFailed(true);
    }
  };
  const startPreview = async () => {
    if (effectiveSelectedWords.size === 0) return;
    setPreviewGenerationFailed(false);
    setStep("preview-generating");
    try {
      const text = await createGeneratedText.mutateAsync({
        createReepriseDto: {
          text_type: "STORY",
          language: "ITALIAN",
          level: "A2",
          tense: "A",
          word_number: 100,
          topic: scene,
          main_character: "",
          edition_word_ids: [...effectiveSelectedWords].slice(0, 6),
          edition_preview: true,
        },
      });
      setPreviewTextId(text.id);
    } catch {
      setPreviewGenerationFailed(true);
    }
  };
  const back = () => {
    if (step === "about") {
      setStep("words");
      return;
    }
    if (step === "words") {
      setStep("idea");
      return;
    }
    if (step === "idea") {
      setHasAcknowledgedIntroduction(false);
      setStep("about");
      return;
    }
    if (step === "length") {
      setStep("words");
      return;
    }
    if (step === "preview") {
      setStep("length");
      return;
    }
    if (step === "preview-generating") {
      setStep("length");
      return;
    }
    setStep("preview");
  };

  return (
    <main
      className="personal-edition-page"
      data-testid="personal-editions-page"
    >
      <div className="personal-edition-phone">
        {step === "about" && (
          <>
            {!hasAcknowledgedIntroduction ? (
              <PersonalEditionIntroduction
                onDismiss={() => {
                  setHasAcknowledgedIntroduction(true);
                  setStep("idea");
                }}
                onBack={() => void navigate({ to: "/app" })}
              />
            ) : null}
          </>
        )}

        {step === "words" && (
          <WordSelectionStep
            words={savedItalianWords}
            selectedWords={effectiveSelectedWords}
            onToggle={toggleWord}
            onBack={back}
            onContinue={() => setStep("length")}
          />
        )}

        {step === "idea" && (
          <>
            <PhoneTop back onBack={back} />
            <IdeaEditor
              scene={scene}
              suggestions={storyIdeaSuggestions}
              onSceneChange={setScene}
              onContinue={() => setStep("words")}
            />
          </>
        )}

        {step === "length" && (
          <>
            <PhoneTop back onBack={back} />
            <LengthSelection length={length} onLengthChange={setLength} />
            <div className="personal-edition-flow-action">
              <PrimaryButton
                testId="personal-editions-start"
                onClick={startPreview}
              >
                {translate("personal_editions_flow_read_sample")}
              </PrimaryButton>
            </div>
          </>
        )}

        {step === "preview" && (
          <>
            <PhoneTop back onBack={back} />
            <section className="personal-edition-preview">
              <article className="personal-edition-sample-reader">
                <header>
                  <b>{translate("personal_editions_preview_reader_label")}</b>
                  <span>{translate("personal_editions_preview_reader_language")}</span>
                </header>
                <h1>{previewTitle}</h1>
                {previewStory.map((paragraph) => (
                  <p key={paragraph}>
                    {highlightEditionWords(paragraph, selectedWordTerms)}
                  </p>
                ))}
              </article>
              <section className="personal-edition-preview__decision">
                <h2>{translate("personal_editions_preview_question")}</h2>
                <p>{translate("personal_editions_preview_adapt")}</p>
                <button
                  data-testid="personal-editions-adapt"
                  type="button"
                  className="personal-edition-preview__adapt"
                  onClick={() => setStep("idea")}
                >
                  {translate("personal_editions_adapt")}
                  <PencilIcon />
                </button>
              </section>
            </section>
            <div className="personal-edition-preview__action personal-edition-flow-action">
              <PrimaryButton
                testId="personal-editions-generate"
                onClick={startGeneration}
              >
                {translate("personal_editions_flow_write")}
              </PrimaryButton>
            </div>
          </>
        )}

        {(step === "generating" || step === "preview-generating") && (
          <>
            <PhoneTop back onBack={() => setStep("length")} />
            <section className="personal-edition-generating">
              <h1>
                {step === "preview-generating"
                  ? translate("personal_editions_preview_generating_title")
                  : translate("personal_editions_generating_title")}
              </h1>
              <p>
                {step === "preview-generating"
                  ? translate("personal_editions_preview_generating_body")
                  : translate("personal_editions_generating_body")}
              </p>
              <div className="personal-edition-generating__visual" aria-hidden="true">
                <span className="personal-edition-generating__paper personal-edition-generating__paper--one" />
                <span className="personal-edition-generating__paper personal-edition-generating__paper--two" />
                <div className="personal-edition-generating__cover">
                  <BookCover
                    title={
                      step === "preview-generating"
                        ? "Your sample"
                        : "Personal Reading Edition"
                    }
                    palette={palette}
                  />
                </div>
                <span className="personal-edition-generating__spark">✦</span>
              </div>
              <div className="personal-edition-generating__progress" aria-hidden="true">
                <i />
                <i />
                <i />
              </div>
              {(step === "preview-generating"
                ? previewGenerationFailed
                : generationFailed) ? (
                <button
                  type="button"
                  className="personal-edition-primary-button"
                  onClick={
                    step === "preview-generating" ? startPreview : startGeneration
                  }
                >
                  Try again
                  <ArrowRightIcon />
                </button>
              ) : null}
            </section>
          </>
        )}

        {step === "ready" && (
          <>
            <PhoneTop back onBack={back} />
            <section className="personal-edition-ready">
              <h1>{translate("personal_editions_ready_title")}</h1>
              <h2>{editionTitle}</h2>
              <div className="personal-edition-ready-cover">
                <BookCover title={editionTitle} palette={palette} />
              </div>
              <div
                className="personal-edition-palette"
                aria-label="Cover color"
              >
                <button
                  onClick={() => setPalette("violet")}
                  className={palette === "violet" ? "is-selected" : ""}
                />
                <button
                  onClick={() => setPalette("coral")}
                  className={palette === "coral" ? "is-selected" : ""}
                />
                <button
                  onClick={() => setPalette("sage")}
                  className={palette === "sage" ? "is-selected" : ""}
                />
              </div>
              <button
                data-testid="personal-editions-order-print"
                type="button"
                className="personal-edition-edition-action"
              >
                <CubeTransparentIcon />
                <span>
                  {translate("personal_editions_order_title")}
                  <small>{translate("personal_editions_order_body")}</small>
                </span>
                <ArrowRightIcon />
              </button>
              <button
                data-testid="personal-editions-read"
                type="button"
                className="personal-edition-edition-action"
                onClick={() => {
                  setReaderPage(0);
                  setStep("reader");
                }}
              >
                <BookOpenIcon />
                <span>
                  {translate("personal_editions_read_title")}
                  <small>{translate("personal_editions_read_body")}</small>
                </span>
                <ArrowRightIcon />
              </button>
              <button type="button" className="personal-edition-edition-action">
                {translate("personal_editions_more_options")}
                <ArrowRightIcon />
              </button>
            </section>
          </>
        )}

        {step === "reader" && (
          <>
            <PhoneTop back onBack={() => setStep("ready")} />
            <article className="personal-edition-reader">
              <header>
                <b>Chapter 1</b>
                <span>
                  {readerPage + 1} / {readerPages.length}
                </span>
              </header>
              {readerPage === 0 ? <h1>{editionTitle}</h1> : null}
              <p>{readerPages[readerPage]}</p>
              {readerPage === 1 ? (
                <aside className="personal-edition-reader-print-prompt">
                  <p>{translate("personal_editions_reader_print_prompt")}</p>
                  <button type="button">
                    <CubeTransparentIcon />
                    {translate("personal_editions_reader_print_action")}
                  </button>
                </aside>
              ) : null}
              <nav aria-label="Edition pages" className="personal-edition-reader-pagination">
                <button
                  type="button"
                  disabled={readerPage === 0}
                  onClick={() => setReaderPage((page) => Math.max(0, page - 1))}
                >
                  {translate("personal_editions_reader_previous")}
                </button>
                <button
                  type="button"
                  disabled={readerPage === readerPages.length - 1}
                  onClick={() =>
                    setReaderPage((page) =>
                      Math.min(readerPages.length - 1, page + 1),
                    )
                  }
                >
                  {translate("personal_editions_reader_continue")}
                  <ArrowRightIcon />
                </button>
              </nav>
            </article>
            <div className="personal-edition-reader-tools">
              <span>Aa</span>
              <span>♧</span>
              <span>Translate</span>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
